/**
 * Shared incremental-merge engine for the Hatif WhatsApp timeline.
 *
 * Extracted from `useWhatsAppConversation`'s inline `syncNewEvents` so two
 * callers can share state:
 *   1. The hook (per-conversation, runs when the active page settles
 *      optimistic sends).
 *   2. The app-level `useHatifWebhookCacheRouter` (one company-wide
 *      Realtime sub that fans out to ANY cached conversation).
 *
 * Why module-scope state: each conversationId needs its own
 * in-flight / pending pair. If both callers fire for the same
 * conversation at once they collapse into one drain loop (single-flight)
 * — separate-instance state would let them race and double-prepend.
 *
 * Algorithm:
 *   - Read the cached timeline.
 *   - Page Voxa's timeline from the top.
 *   - For each event: known + same status → boundary, stop. Known +
 *     different status → patch in place. Unknown → push to newEvents.
 *   - Merge via `setQueryData` (silent — no observer-triggered refetch).
 *   - On error or pagination exhaustion → invalidate (the only path that
 *     shows a loading state, intentionally last-resort).
 */
import type { HatifConversationTimelineEvent } from '@dallali/notifications';
import type {
  InfiniteData,
  QueryClient,
  QueryKey,
} from '@tanstack/react-query';

import { hatifGetConversationTimeline } from '@/services/api';

interface TimelinePage {
  items: HatifConversationTimelineEvent[];
  skip: number;
  originalSize: number;
  total: number;
}

interface SyncState {
  inFlight: boolean;
  pending: boolean;
}

// Single-flight state per conversation, shared across consumers.
const STATE = new Map<string, SyncState>();

const SYNC_PAGE = 10;
const MAX_PAGES = 5; // up to 50 events of catch-up before invalidate fallback

// Monotonic ordering on Voxa's WhatsApp delivery states. The merge below
// only patches a bubble when the incoming status RANKS STRICTLY HIGHER than
// what the cache already has — never a downgrade. This mirrors the guard on
// the server-side `notification_sends` correlation (`.in(['pending','sent'])`
// at hatif.webhook.service.ts:163) so the UI and the audit log can't
// disagree on terminal state.
//
// Why this matters: a webhook fires for every status change, and each fire
// causes us to refetch Voxa's timeline. If Voxa's REST API happens to lag
// and return a stale (older) status during a race — e.g. the 'Pending'
// state for a message we've already optimistically settled as 'Sent', or
// a late 'Sent' arriving after we've already seen 'Read' — the previous
// behavior would happily patch the bubble backwards. Forward-only fixes
// that without complicating the merge.
//
// `Failed` sits above the success states because once delivery has failed
// the user should see that signal even if a stale success raced past.
// Unknown statuses (a hypothetical new Voxa value, e.g. `Queued`) get
// rank `-1` — same as `undefined`. That way an unfamiliar new status
// will never silently block a legitimate promotion: if the cached side
// holds an unknown value, ANY known status will promote past it. The
// only case where nothing happens is "both sides unknown," which is
// already a no-op.
const STATUS_RANK: Record<string, number> = {
  Pending: 0,
  Sent: 1,
  Delivered: 2,
  Read: 3,
  Failed: 99,
};
const statusRank = (status: string | undefined): number =>
  status === undefined ? -1 : (STATUS_RANK[status] ?? -1);

export interface SyncTimelineCacheArgs {
  queryClient: QueryClient;
  companyId: string;
  conversationId: string;
  timelineQueryKey: QueryKey;
}

export async function syncTimelineCache(
  args: SyncTimelineCacheArgs
): Promise<void> {
  const { conversationId } = args;
  const state = STATE.get(conversationId) ?? {
    inFlight: false,
    pending: false,
  };
  STATE.set(conversationId, state);

  if (state.inFlight) {
    state.pending = true;
    return;
  }
  state.inFlight = true;

  try {
    // Drain loop: any caller that fires during runOnce() flips pending
    // so the runner re-runs without losing the trigger.
    do {
      state.pending = false;
      await runOnce(args);
    } while (state.pending);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'unknown error';
    console.warn(
      `syncTimelineCache(${conversationId}) failed (${msg}); falling back to invalidate`
    );
    args.queryClient.invalidateQueries({ queryKey: args.timelineQueryKey });
  } finally {
    state.inFlight = false;
    // Defensive: if a caller flipped pending after the drain check but
    // before we cleared in-flight, re-fire async so it isn't lost.
    if (state.pending) {
      state.pending = false;
      void Promise.resolve().then(() => syncTimelineCache(args));
    }
  }
}

async function runOnce({
  queryClient,
  companyId,
  conversationId,
  timelineQueryKey,
}: SyncTimelineCacheArgs): Promise<void> {
  const current =
    queryClient.getQueryData<InfiniteData<TimelinePage>>(timelineQueryKey);
  if (!current || current.pages.length === 0) {
    // No cached timeline → nothing to merge into. The initial fetch
    // (when the user lands on the page) will pull the latest anyway.
    return;
  }

  const knownStatus = new Map<string, string>();
  for (const page of current.pages) {
    for (const event of page.items) {
      knownStatus.set(event.eventId, event.status);
    }
  }

  const newEvents: HatifConversationTimelineEvent[] = [];
  const statusUpdates: HatifConversationTimelineEvent[] = [];
  let total = current.pages[0]?.total ?? 0;

  outer: for (let p = 0; p < MAX_PAGES; p += 1) {
    const result = await hatifGetConversationTimeline(
      companyId,
      conversationId,
      {
        SkipCount: p * SYNC_PAGE,
        MaxResultCount: SYNC_PAGE,
      }
    );
    total = result.totalCount;
    const items = result.items ?? [];
    for (const event of items) {
      const oldStatus = knownStatus.get(event.eventId);
      if (oldStatus !== undefined) {
        // Known event — only patch if Voxa's status is a strict promotion.
        // Equal statuses are no-ops; strictly-lower (stale) statuses are
        // dropped to avoid downgrading a bubble (see STATUS_RANK rationale).
        if (statusRank(event.status) > statusRank(oldStatus)) {
          statusUpdates.push(event);
        }
        break outer;
      }
      newEvents.push(event);
    }
    if (items.length < SYNC_PAGE) break; // end of Voxa's timeline
  }

  if (newEvents.length === 0 && statusUpdates.length === 0) return;

  queryClient.setQueryData<InfiniteData<TimelinePage>>(
    timelineQueryKey,
    (old) => {
      if (!old) return old;

      // Re-derive what's actually in the cache RIGHT NOW. `newEvents` was
      // computed against a snapshot taken at the top of runOnce, which
      // could be stale by hundreds of milliseconds — long enough for
      // `settleOptimistic` (renaming temp-XYZ → canonical), another
      // syncTimelineCache that slipped past the single-flight gate, or
      // any other cache mutation to land. Without this re-check we'd
      // happily prepend an event that's already in `old` and the user
      // would see two bubbles for the same message. The webhook race
      // shape that prompted this: Pending webhook arrives before our
      // outbound POST returns, Voxa REST returns the canonical event
      // mid-await, settleOptimistic renames the temp in the meantime —
      // runOnce's stale snapshot wouldn't know the canonical id existed
      // yet.
      const liveIds = new Set<string>();
      for (const page of old.pages) {
        for (const event of page.items) liveIds.add(event.eventId);
      }
      const freshNewEvents = newEvents.filter((e) => !liveIds.has(e.eventId));

      // Returning `old` unchanged tells React Query "nothing to broadcast",
      // so subscribers don't re-render for a no-op update.
      if (freshNewEvents.length === 0 && statusUpdates.length === 0) {
        return old;
      }

      const pages = old.pages.map((page) => ({
        ...page,
        items: page.items.slice(),
        total,
      }));
      if (freshNewEvents.length > 0) {
        pages[0] = {
          ...pages[0],
          items: [...freshNewEvents, ...pages[0].items],
        };
      }
      if (statusUpdates.length > 0) {
        const patchById = new Map(
          statusUpdates.map((e) => [e.eventId, e] as const)
        );
        for (let i = 0; i < pages.length; i += 1) {
          pages[i].items = pages[i].items.map(
            (event) => patchById.get(event.eventId) ?? event
          );
        }
      }
      return { ...old, pages };
    }
  );
}

/** Test-only: reset module-scope state. */
export function __resetSyncTimelineCacheForTests(): void {
  STATE.clear();
}
